import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { dashboardAPI, actionItemAPI, corporateAPI } from '../api';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  Calendar, FileText, CheckSquare, Award,
  Users, Loader2, ChevronRight, BarChart3, TrendingUp, PieChart,
  Globe2, Vote, FileCheck, MessageSquare, ClipboardList,
  Shield, Upload, ExternalLink, BookOpen, AlertTriangle, CheckCircle, Clock, Timer
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { toast } from 'sonner';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart as RechartsPie, Pie, Cell, AreaChart, Area,
} from 'recharts';

const PIE_COLORS_RES = ['#10b981', '#6366f1', '#F58A26', '#ef4444'];
const PIE_COLORS_ACT = ['#10b981', '#F58A26', '#00A3A3', '#ef4444'];

const Dashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({ upcoming_meetings: 0, draft_minutes: 0, open_action_items: 0, pending_resolutions: 0 });
  const [myTasks, setMyTasks] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [requirements, setRequirements] = useState([]);
  const [deadlines, setDeadlines] = useState([]);
  const [loading, setLoading] = useState(true);

  const isSecretariat = ['corporate_secretary', 'secretariat_staff', 'super_admin', 'landlord'].includes(user?.role);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, tasksRes, analyticsRes, reqRes, dlRes] = await Promise.all([
          dashboardAPI.getStats(),
          actionItemAPI.getMyTasks(),
          dashboardAPI.getAnalytics().catch(() => ({ data: null })),
          corporateAPI.getRequirements().catch(() => ({ data: [] })),
          dashboardAPI.getDeadlines().catch(() => ({ data: [] })),
        ]);
        setStats(statsRes.data);
        setMyTasks(tasksRes.data.slice(0, 5));
        if (analyticsRes.data) setAnalytics(analyticsRes.data);
        setRequirements(reqRes.data || []);
        setDeadlines(dlRes.data || []);
      } catch (err) {
        console.error('Failed to fetch dashboard data:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const getRequirementStatus = (req) => {
    if (req.status === 'completed') return { label: 'Completed', color: 'bg-green-100 text-green-800', icon: CheckCircle };
    const due = new Date(req.due_date);
    const now = new Date();
    const daysLeft = Math.ceil((due - now) / (1000 * 60 * 60 * 24));
    if (daysLeft < 0) return { label: `${Math.abs(daysLeft)}d overdue`, color: 'bg-red-100 text-red-800', icon: AlertTriangle };
    if (daysLeft === 0) return { label: 'Due today', color: 'bg-red-100 text-red-800', icon: AlertTriangle };
    if (daysLeft <= 7) return { label: `${daysLeft}d left`, color: 'bg-red-100 text-red-700', icon: AlertTriangle };
    if (daysLeft <= 30) return { label: `${daysLeft}d left`, color: 'bg-amber-100 text-amber-800', icon: Clock };
    if (daysLeft <= 90) return { label: `${daysLeft}d left`, color: 'bg-blue-100 text-blue-800', icon: Clock };
    return { label: `${daysLeft}d left`, color: 'bg-slate-100 text-slate-600', icon: Calendar };
  };

  const [adoptingDefaults, setAdoptingDefaults] = useState(false);
  const handleAdoptDefaults = async () => {
    setAdoptingDefaults(true);
    try {
      const res = await corporateAPI.adoptDefaults();
      toast.success(`${res.data.count} SEC default requirements added`);
      const reqRes = await corporateAPI.getRequirements();
      setRequirements(reqRes.data || []);
    } catch (err) {
      toast.error('Failed to adopt defaults');
    } finally { setAdoptingDefaults(false); }
  };

  const getPriorityColor = (p) => ({ low: 'text-gray-500', medium: 'text-blue-600', high: 'text-orange-500', urgent: 'text-red-600' }[p] || 'text-gray-500');

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
      <div className="bg-white border border-[#E2E6EA] rounded-lg shadow-lg px-3 py-2 text-xs">
        <p className="font-semibold text-[#1A1D21] mb-1">{label}</p>
        {payload.map((p, i) => (
          <p key={i} style={{ color: p.color }} className="flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full inline-block" style={{ background: p.color }} />{p.name}: <strong>{p.value}</strong>
          </p>
        ))}
      </div>
    );
  };

  if (loading) {
    return <div className="flex items-center justify-center min-h-[60vh]"><Loader2 className="h-8 w-8 animate-spin text-[#00A3A3]" /></div>;
  }

  const resolutionPieData = analytics?.resolution_stats ? [
    { name: 'Approved', value: analytics.resolution_stats.approved },
    { name: 'Under Review', value: analytics.resolution_stats.under_review },
    { name: 'Draft', value: analytics.resolution_stats.draft },
    { name: 'Rejected', value: analytics.resolution_stats.rejected },
  ].filter(d => d.value > 0) : [];

  const actionPieData = analytics?.action_item_stats ? [
    { name: 'Completed', value: analytics.action_item_stats.completed },
    { name: 'In Progress', value: analytics.action_item_stats.in_progress },
    { name: 'Pending', value: analytics.action_item_stats.pending },
    { name: 'Overdue', value: analytics.action_item_stats.overdue },
  ].filter(d => d.value > 0) : [];

  return (
    <div className="space-y-6" data-testid="dashboard">
      <div>
        <h1 className="text-2xl md:text-3xl font-bold text-[#1A1D21]">Welcome back, {user?.full_name?.split(' ')[0]}</h1>
        <p className="text-[#5A6570] mt-1">Here's what's happening with your governance activities</p>
      </div>

      {/* Stat Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { key: 'stat-upcoming-meetings', label: 'Upcoming Meetings', value: stats.upcoming_meetings, icon: Calendar, color: '#00A3A3', route: '/meetings' },
          { key: 'stat-draft-minutes', label: 'Draft Minutes', value: stats.draft_minutes, icon: FileText, color: '#F58A26', route: '/minutes' },
          { key: 'stat-action-items', label: 'Open Tasks', value: stats.open_action_items, icon: CheckSquare, color: '#00A3A3', route: '/action-items' },
          { key: 'stat-pending-resolutions', label: 'Pending Resolutions', value: stats.pending_resolutions, icon: Award, color: '#F58A26', route: '/resolutions' },
        ].map(s => (
          <Card key={s.key} className="border-[#E2E6EA] hover:border-[#00A3A3] transition-colors cursor-pointer" data-testid={s.key} onClick={() => navigate(s.route)}>
            <CardContent className="p-5">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm text-[#5A6570] font-medium">{s.label}</p>
                  <p className="text-3xl font-bold text-[#1A1D21] mt-1">{s.value}</p>
                </div>
                <div className="p-2 rounded-lg" style={{ background: `${s.color}15` }}><s.icon className="h-5 w-5" style={{ color: s.color }} /></div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Tabs: General / Analytics */}
      <Tabs defaultValue={isSecretariat ? "general" : "analytics"} className="space-y-4">
        {isSecretariat && (
        <TabsList className="bg-[#F0F2F4] p-1">
          <TabsTrigger value="general" className="data-[state=active]:bg-white" data-testid="tab-general">
            <ClipboardList className="h-4 w-4 mr-2" />General
          </TabsTrigger>
          <TabsTrigger value="analytics" className="data-[state=active]:bg-white" data-testid="tab-analytics">
            <BarChart3 className="h-4 w-4 mr-2" />Analytics
          </TabsTrigger>
        </TabsList>
        )}

        {/* === GENERAL TAB === */}
        <TabsContent value="general" className="space-y-4">
          {/* 1. My Tasks */}
          <Card className="border-[#E2E6EA]">
            <CardHeader className="flex flex-row items-center justify-between pb-3">
              <CardTitle className="text-lg font-semibold">My Tasks</CardTitle>
              <Link to="/action-items" className="text-sm text-[#00A3A3] hover:underline flex items-center">View all <ChevronRight className="h-4 w-4" /></Link>
            </CardHeader>
            <CardContent>
              {myTasks.length === 0 ? (
                <div className="text-center py-8 text-[#5A6570]"><CheckSquare className="h-10 w-10 mx-auto mb-3 text-[#A8B1B9]" /><p>No pending tasks</p></div>
              ) : (
                <div className="space-y-2.5">
                  {myTasks.map((task) => (
                    <div key={task.id} className="p-3 rounded-lg border border-[#E2E6EA] hover:border-[#00A3A3] transition-colors">
                      <div className="flex items-start gap-2.5">
                        <CheckSquare className={`h-4 w-4 mt-0.5 shrink-0 ${getPriorityColor(task.priority)}`} />
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-[#1A1D21] text-sm truncate">{task.description}</p>
                          <div className="flex items-center gap-2 mt-1">
                            <span className="text-[11px] text-[#A8B1B9]">Due: {format(parseISO(task.due_date), 'MMM d')}</span>
                            <span className={`text-[10px] font-semibold uppercase ${getPriorityColor(task.priority)}`}>{task.priority}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* 2. Upcoming Deadlines (orange box) */}
          <Card className="border-[#F58A26]/30 bg-[#FFF7ED]" data-testid="deadlines-card">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-lg font-semibold flex items-center gap-2 text-[#C2742B]">
                <Timer className="h-5 w-5" />Upcoming Deadlines
              </CardTitle>
              <span className="text-[10px] text-[#A8B1B9]">{deadlines.length} item{deadlines.length !== 1 ? 's' : ''}</span>
            </CardHeader>
            <CardContent>
              {deadlines.length === 0 ? (
                <div className="text-center py-6 text-[#5A6570]">
                  <Timer className="h-8 w-8 mx-auto mb-2 text-[#A8B1B9]" />
                  <p className="text-sm">No upcoming deadlines</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {deadlines.slice(0, 8).map((dl) => {
                    const due = dl.due_date ? new Date(dl.due_date) : null;
                    const now = new Date();
                    const daysLeft = due ? Math.ceil((due - now) / (1000 * 60 * 60 * 24)) : null;
                    const isOverdue = daysLeft !== null && daysLeft < 0;
                    const isUrgent = daysLeft !== null && daysLeft >= 0 && daysLeft <= 3;
                    const sourceColors = {
                      meeting: { bg: 'bg-[#00A3A3]/10', text: 'text-[#00A3A3]', border: 'border-[#00A3A3]/20' },
                      action_item: { bg: 'bg-[#6366f1]/10', text: 'text-[#6366f1]', border: 'border-[#6366f1]/20' },
                      minutes: { bg: 'bg-[#F58A26]/10', text: 'text-[#F58A26]', border: 'border-[#F58A26]/20' },
                      resolution: { bg: 'bg-[#10b981]/10', text: 'text-[#10b981]', border: 'border-[#10b981]/20' },
                    };
                    const sc = sourceColors[dl.source] || sourceColors.meeting;
                    const SourceIcon = dl.source === 'meeting' ? Calendar : dl.source === 'action_item' ? CheckSquare : dl.source === 'minutes' ? FileText : Award;

                    return (
                      <div key={`${dl.source}-${dl.id}`}
                        className={`p-2.5 rounded-lg border ${isOverdue ? 'bg-red-50 border-red-200' : isUrgent ? 'bg-amber-50 border-amber-200' : 'bg-white border-[#F58A26]/15'} hover:shadow-sm transition-shadow cursor-pointer`}
                        onClick={() => dl.route && navigate(dl.route)}
                        data-testid={`deadline-${dl.source}-${dl.id}`}
                      >
                        <div className="flex items-start gap-2.5">
                          <SourceIcon className={`h-4 w-4 mt-0.5 shrink-0 ${isOverdue ? 'text-red-500' : sc.text}`} />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-[#1A1D21] truncate">{dl.title}</p>
                            <div className="flex items-center gap-2 mt-0.5">
                              <span className={`text-[10px] px-1.5 py-0.5 rounded ${sc.bg} ${sc.text} font-medium`}>{dl.source_label}</span>
                              {due && <span className="text-[10px] text-[#A8B1B9]">{format(due, 'MMM d, yyyy')}</span>}
                              {dl.assignee && <span className="text-[10px] text-[#A8B1B9]">{dl.assignee}</span>}
                            </div>
                          </div>
                          {daysLeft !== null && (
                            <Badge className={`text-[10px] shrink-0 ${isOverdue ? 'bg-red-100 text-red-800' : isUrgent ? 'bg-amber-100 text-amber-800' : daysLeft <= 30 ? 'bg-blue-100 text-blue-800' : 'bg-slate-100 text-slate-600'}`}>
                              {isOverdue ? `${Math.abs(daysLeft)}d overdue` : daysLeft === 0 ? 'Today' : `${daysLeft}d left`}
                            </Badge>
                          )}
                        </div>
                      </div>
                    );
                  })}
                  {deadlines.length > 8 && (
                    <p className="text-xs text-[#F58A26] text-center mt-1">+{deadlines.length - 8} more deadlines</p>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* 3. Reportorial Requirements */}
          <Card className="border-[#E2E6EA]" data-testid="reportorial-requirements-card">
            <CardHeader className="flex flex-row items-center justify-between pb-3">
              <CardTitle className="text-lg font-semibold flex items-center gap-2">
                <ClipboardList className="h-5 w-5 text-[#F58A26]" />Reportorial Requirements
              </CardTitle>
              <div className="flex items-center gap-2">
                {requirements.length === 0 && (
                  <Button variant="outline" size="sm" className="text-xs border-[#F58A26] text-[#F58A26] hover:bg-[#F58A26]/10" onClick={handleAdoptDefaults} disabled={adoptingDefaults} data-testid="adopt-defaults-btn">
                    {adoptingDefaults ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <ClipboardList className="h-3 w-3 mr-1" />}
                    Adopt SEC Defaults
                  </Button>
                )}
                <Link to="/admin" className="text-xs text-[#00A3A3] hover:underline">Manage</Link>
              </div>
            </CardHeader>
            <CardContent>
              {requirements.length === 0 ? (
                <div className="text-center py-6 text-[#5A6570]">
                  <ClipboardList className="h-8 w-8 mx-auto mb-2 text-[#A8B1B9]" />
                  <p className="text-sm">No requirements set</p>
                  <Link to="/admin"><Button variant="outline" size="sm" className="mt-2 text-xs border-[#00A3A3] text-[#00A3A3]">Add in Settings</Button></Link>
                </div>
              ) : (
                <div className="space-y-2">
                  {requirements.slice(0, 5).map((req) => {
                    const st = getRequirementStatus(req);
                    return (
                      <div key={req.id} className="flex items-center justify-between p-2.5 rounded-lg border border-[#E2E6EA] hover:border-[#F58A26]/40 transition-colors">
                        <div className="flex items-center gap-2 min-w-0 flex-1">
                          <st.icon className={`h-4 w-4 shrink-0 ${req.status === 'completed' ? 'text-green-600' : st.label === 'Overdue' ? 'text-red-500' : 'text-amber-500'}`} />
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-[#1A1D21] truncate">{req.name}</p>
                            <p className="text-[11px] text-[#A8B1B9]">Due: {format(parseISO(req.due_date), 'MMM d, yyyy')} · {req.recurrence}</p>
                          </div>
                        </div>
                        <Badge className={`text-[10px] shrink-0 ${st.color}`}>{st.label}</Badge>
                      </div>
                    );
                  })}
                  {requirements.length > 5 && (
                    <Link to="/admin" className="text-xs text-[#00A3A3] hover:underline block text-center mt-1">
                      +{requirements.length - 5} more
                    </Link>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* === ANALYTICS TAB === */}
        <TabsContent value="analytics" className="space-y-4">
          {analytics ? (
            <>
              {/* Monthly Activity Trend */}
              <Card className="border-[#E2E6EA]" data-testid="monthly-activity-chart">
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg font-semibold flex items-center gap-2">
                    <TrendingUp className="h-5 w-5 text-[#00A3A3]" />Monthly Activity Trend
                  </CardTitle>
                  <p className="text-xs text-[#A8B1B9]">Meetings, resolutions and action items over the last 6 months</p>
                </CardHeader>
                <CardContent>
                  <div className="h-[250px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={analytics.monthly_activity} margin={{ top: 5, right: 10, left: -15, bottom: 0 }}>
                        <defs>
                          <linearGradient id="gMeetings" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#00A3A3" stopOpacity={0.3} /><stop offset="95%" stopColor="#00A3A3" stopOpacity={0} /></linearGradient>
                          <linearGradient id="gResolutions" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#F58A26" stopOpacity={0.3} /><stop offset="95%" stopColor="#F58A26" stopOpacity={0} /></linearGradient>
                          <linearGradient id="gActions" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#6366f1" stopOpacity={0.3} /><stop offset="95%" stopColor="#6366f1" stopOpacity={0} /></linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#E2E6EA" />
                        <XAxis dataKey="month" tick={{ fontSize: 12, fill: '#5A6570' }} axisLine={{ stroke: '#E2E6EA' }} />
                        <YAxis tick={{ fontSize: 12, fill: '#5A6570' }} axisLine={{ stroke: '#E2E6EA' }} allowDecimals={false} />
                        <Tooltip content={<CustomTooltip />} />
                        <Area type="monotone" dataKey="meetings" name="Meetings" stroke="#00A3A3" fillOpacity={1} fill="url(#gMeetings)" strokeWidth={2} />
                        <Area type="monotone" dataKey="resolutions" name="Resolutions" stroke="#F58A26" fillOpacity={1} fill="url(#gResolutions)" strokeWidth={2} />
                        <Area type="monotone" dataKey="action_items" name="Action Items" stroke="#6366f1" fillOpacity={1} fill="url(#gActions)" strokeWidth={2} />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="flex justify-center gap-5 mt-2">
                    {[{ label: 'Meetings', color: '#00A3A3' }, { label: 'Resolutions', color: '#F58A26' }, { label: 'Action Items', color: '#6366f1' }].map(l => (
                      <span key={l.label} className="flex items-center gap-1.5 text-xs text-[#5A6570]">
                        <span className="w-2.5 h-2.5 rounded-full" style={{ background: l.color }} />{l.label}
                      </span>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Resolution + Action Item charts */}
              <div className="grid md:grid-cols-2 gap-4">
                <Card className="border-[#E2E6EA]" data-testid="resolution-stats-chart">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base font-semibold flex items-center gap-2"><PieChart className="h-4 w-4 text-[#F58A26]" />Resolution Status</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center gap-4">
                      <div className="h-[160px] w-[160px] shrink-0">
                        {resolutionPieData.length > 0 ? (
                          <ResponsiveContainer width="100%" height="100%">
                            <RechartsPie><Pie data={resolutionPieData} cx="50%" cy="50%" innerRadius={40} outerRadius={70} paddingAngle={3} dataKey="value">
                              {resolutionPieData.map((_, i) => <Cell key={i} fill={PIE_COLORS_RES[i % PIE_COLORS_RES.length]} />)}
                            </Pie><Tooltip /></RechartsPie>
                          </ResponsiveContainer>
                        ) : <div className="h-full flex items-center justify-center text-xs text-[#A8B1B9]">No data</div>}
                      </div>
                      <div className="flex-1 space-y-2">
                        <div className="flex items-center justify-between"><span className="text-xs text-[#5A6570]">Total</span><span className="text-sm font-bold text-[#1A1D21]">{analytics.resolution_stats.total}</span></div>
                        {[{ label: 'Approved', color: '#10b981', val: analytics.resolution_stats.approved }, { label: 'Under Review', color: '#6366f1', val: analytics.resolution_stats.under_review }, { label: 'Draft', color: '#F58A26', val: analytics.resolution_stats.draft }].map(r => (
                          <div key={r.label} className="flex items-center justify-between"><span className="text-xs flex items-center gap-1.5"><span className="w-2 h-2 rounded-full" style={{ background: r.color }} />{r.label}</span><span className="text-sm font-semibold text-[#1A1D21]">{r.val}</span></div>
                        ))}
                        <div className="h-px bg-[#E2E6EA] my-1" />
                        <div className="flex items-center justify-between"><span className="text-xs text-[#5A6570]">Approval Rate</span><span className="text-sm font-bold text-[#10b981]">{analytics.resolution_stats.approval_rate}%</span></div>
                        <div className="flex gap-2 text-[10px]">
                          <span className="px-1.5 py-0.5 bg-blue-50 text-blue-700 rounded">Meeting: {analytics.resolution_stats.by_meeting}</span>
                          <span className="px-1.5 py-0.5 bg-purple-50 text-purple-700 rounded">Referendum: {analytics.resolution_stats.by_referendum}</span>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-[#E2E6EA]" data-testid="action-item-stats-chart">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base font-semibold flex items-center gap-2"><BarChart3 className="h-4 w-4 text-[#00A3A3]" />Action Items</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center gap-4">
                      <div className="h-[160px] w-[160px] shrink-0">
                        {actionPieData.length > 0 ? (
                          <ResponsiveContainer width="100%" height="100%">
                            <RechartsPie><Pie data={actionPieData} cx="50%" cy="50%" innerRadius={40} outerRadius={70} paddingAngle={3} dataKey="value">
                              {actionPieData.map((_, i) => <Cell key={i} fill={PIE_COLORS_ACT[i % PIE_COLORS_ACT.length]} />)}
                            </Pie><Tooltip /></RechartsPie>
                          </ResponsiveContainer>
                        ) : <div className="h-full flex items-center justify-center text-xs text-[#A8B1B9]">No data</div>}
                      </div>
                      <div className="flex-1 space-y-2">
                        <div className="flex items-center justify-between"><span className="text-xs text-[#5A6570]">Total</span><span className="text-sm font-bold text-[#1A1D21]">{analytics.action_item_stats.total}</span></div>
                        {[{ label: 'Completed', color: '#10b981', val: analytics.action_item_stats.completed }, { label: 'In Progress', color: '#00A3A3', val: analytics.action_item_stats.in_progress }, { label: 'Pending', color: '#F58A26', val: analytics.action_item_stats.pending }, { label: 'Overdue', color: '#ef4444', val: analytics.action_item_stats.overdue }].map(a => (
                          <div key={a.label} className="flex items-center justify-between"><span className="text-xs flex items-center gap-1.5"><span className="w-2 h-2 rounded-full" style={{ background: a.color }} />{a.label}</span><span className={`text-sm font-semibold ${a.label === 'Overdue' ? 'text-red-600' : 'text-[#1A1D21]'}`}>{a.val}</span></div>
                        ))}
                        <div className="h-px bg-[#E2E6EA] my-1" />
                        <div className="flex items-center justify-between"><span className="text-xs text-[#5A6570]">Completion Rate</span><span className="text-sm font-bold text-[#10b981]">{analytics.action_item_stats.completion_rate}%</span></div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Document Overview */}
              {analytics.document_counts && (
                <Card className="border-[#E2E6EA]" data-testid="document-counts">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg font-semibold flex items-center gap-2"><BarChart3 className="h-5 w-5 text-[#00A3A3]" />Document Overview</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                      {[
                        { label: 'Meetings', value: analytics.document_counts.meetings, color: '#00A3A3' },
                        { label: 'Minutes', value: analytics.document_counts.minutes, color: '#F58A26' },
                        { label: 'Resolutions', value: analytics.document_counts.resolutions, color: '#6366f1' },
                        { label: 'Certificates', value: analytics.document_counts.certificates, color: '#10b981' },
                        { label: 'Action Items', value: analytics.document_counts.action_items, color: '#ef4444' },
                      ].map(item => (
                        <div key={item.label} className="text-center p-3 rounded-lg bg-[#FAFBFC] border border-[#E2E6EA]">
                          <p className="text-2xl font-bold" style={{ color: item.color }}>{item.value}</p>
                          <p className="text-xs text-[#5A6570] mt-1">{item.label}</p>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </>
          ) : (
            <Card className="border-[#E2E6EA]">
              <CardContent className="py-12 text-center">
                <BarChart3 className="h-12 w-12 mx-auto mb-3 text-[#A8B1B9]" />
                <p className="text-[#5A6570]">Analytics data will appear as you use the platform</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Dashboard;
